/***
 *
 *   STATUS WORKFLOW COLUMN / BUCKET
 *   Phase 6 — aligned with Phase 4 kanban column chrome. Drag highlight uses
 *   theme tokens (no hardcoded hex) so light + dark both work.
 *
 **********/

import React, { useContext, useEffect, useState } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Icon, ViewContext, cn } from 'components/lib';
import Style from './column.tailwind';
import { useTranslate } from 'app/translations';
import { StatusSettings } from '../settings';

const Column = ({
    columnId,
    column,
    updateTicketStatusMutation,
    statusTemplate,
    dragInfo,
    layout = 'bucket',
}) => {
    const viewContext = useContext(ViewContext);
    const { _t } = useTranslate();
    const isStrip = layout === 'strip' || columnId === 'unassigned';

    const [draggingFromUnassigned, setDraggingFromUnassigned] = useState(false);

    useEffect(() => {
        if (dragInfo.from === 'unassigned' && dragInfo.to !== 'unassigned') {
            setDraggingFromUnassigned(true);
        } else {
            setDraggingFromUnassigned(false);
        }
    }, [dragInfo]);

    if (column.key === 'unassigned' && column.statuses.length === 0) {
        return null;
    }

    const title =
        column.label || _t(`ticket.category.status.${column.key}`) || column.key;

    const openSettings = () => {
        viewContext.pannel.show({
            title: `${title} ${_t('settings.label')}`,
            component: (
                <StatusSettings
                    columnId={columnId}
                    title={title}
                    updateTicketStatusMutation={updateTicketStatusMutation}
                    statusTemplate={statusTemplate}
                />
            ),
            styles: {
                width: 500,
            },
        });
    };

    return (
        <div className={cn(Style.container, isStrip && Style.strip)} key={columnId}>
            <div className={Style.header}>
                <div className={Style.headerRow}>
                    {column.color && (
                        <span
                            className={Style.swatch}
                            style={{ backgroundColor: column.color }}
                        />
                    )}
                    <h3 className={Style.title}>{title}</h3>
                    {!isStrip && (
                        <span className={Style.count}>{column.statuses.length}</span>
                    )}
                </div>
                {!isStrip && (
                    <button type="button" className={Style.actionBtn} onClick={openSettings}>
                        <Icon image="settings" size={16} color="currentColor" />
                    </button>
                )}
            </div>

            <Droppable
                droppableId={columnId}
                key={columnId}
                direction={isStrip ? 'horizontal' : 'vertical'}>
                {(provided, snapshot) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={cn(
                            Style.droppable,
                            isStrip ? Style.droppableStrip : Style.droppableBucket,
                            snapshot.isDraggingOver && Style.droppableActive,
                        )}>
                        {column.statuses.length === 0 && !snapshot.isDraggingOver && (
                            <p className={Style.empty}>—</p>
                        )}
                        {column.statuses.map((item, index) => (
                            <Draggable
                                key={item.id.toString()}
                                draggableId={item.id.toString()}
                                index={index}>
                                {(dragProvided, dragSnapshot) => (
                                    <div
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                        className={cn(
                                            Style.statusItem,
                                            dragSnapshot.isDragging && Style.statusDragging,
                                            isStrip && Style.statusStrip,
                                            draggingFromUnassigned &&
                                                dragSnapshot.isDragging &&
                                                Style.statusFromStrip,
                                        )}
                                        style={dragProvided.draggableProps.style}>
                                        <Icon
                                            image="grip-vertical"
                                            size={12}
                                            color="currentColor"
                                            className={Style.grip}
                                        />
                                        <span className={Style.statusLabel}>{item.name}</span>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </div>
    );
};

export default Column;
